import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { CalendarDays, Clock, X, Loader2, Inbox, Repeat } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const fetchBookings = () => {
    setLoading(true);
    axios.get(`${API}/api/bookings/my`, { withCredentials: true })
      .then(res => setBookings(res.data))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleCancel = async (id, cancelSeries = false) => {
    setCancelling(id);
    try {
      await axios.delete(`${API}/api/bookings/${id}`, {
        params: cancelSeries ? { cancel_series: true } : {},
        withCredentials: true
      });
      toast.success(cancelSeries ? 'Series cancelled' : 'Booking cancelled');
      fetchBookings();
    } catch {
      toast.error('Failed to cancel booking');
    } finally {
      setCancelling(null);
    }
  };

  // Group bookings by group_id for display
  const grouped = [];
  const seen = new Set();
  bookings.forEach(b => {
    if (b.group_id && !seen.has(b.group_id)) {
      seen.add(b.group_id);
      const group = bookings.filter(x => x.group_id === b.group_id);
      grouped.push({ type: 'group', bookings: group, groupId: b.group_id });
    } else if (!b.group_id) {
      grouped.push({ type: 'single', booking: b });
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={24} className="animate-spin text-text-secondary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="my-bookings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tighter text-white" data-testid="my-bookings-heading">
          My Bookings
        </h1>
        <p className="text-text-secondary text-sm mt-2 font-body">
          Your upcoming reservations. Cancel anytime before the slot.
        </p>
      </div>

      {grouped.length === 0 ? (
        <div className="text-center py-20" data-testid="no-bookings">
          <Inbox size={40} className="mx-auto mb-4 text-text-secondary/40" />
          <p className="text-text-secondary font-body">No upcoming bookings.</p>
          <a href="/" className="text-white text-sm font-body underline mt-2 inline-block">Browse resources</a>
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {grouped.map((item, idx) => {
            if (item.type === 'single') {
              const b = item.booking;
              return (
                <BookingRow key={b.id} booking={b} cancelling={cancelling} onCancel={handleCancel} />
              );
            }
            // Group (recurring or multi-slot)
            const first = item.bookings[0];
            const uniqueDates = [...new Set(item.bookings.map(x => x.date))].sort();
            const uniqueSlots = [...new Set(item.bookings.map(x => x.time_slot))].sort();
            return (
              <div key={item.groupId} className="border border-border/40 bg-surface" data-testid={`booking-group-${item.groupId}`}>
                <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/20">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-base font-medium text-white truncate">{first.resource_name}</h3>
                      <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-accent px-1.5 py-0.5 border border-accent/30 bg-accent/10">
                        <Repeat size={10} />
                        {uniqueDates.length > 1 ? `${uniqueDates.length} dates` : `${uniqueSlots.length}h block`}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="text-xs font-mono text-text-secondary">
                        {uniqueSlots[0]} — {(parseInt(uniqueSlots[uniqueSlots.length - 1].split(':')[0]) + 1).toString().padStart(2, '0')}:00
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancel(first.id, true)}
                    disabled={cancelling === first.id}
                    data-testid={`cancel-series-${item.groupId}`}
                    className="px-3 py-1.5 border border-booked/30 text-booked text-xs font-mono uppercase tracking-wider hover:bg-booked/10 transition-all disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                  >
                    {cancelling === first.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                    Cancel All
                  </button>
                </div>
                <div className="px-4 sm:px-5 py-2 space-y-1">
                  {uniqueDates.map(d => (
                    <div key={d} className="flex items-center gap-3 py-1 text-xs font-mono text-text-secondary">
                      <CalendarDays size={11} strokeWidth={1.5} />
                      {format(parseISO(d), 'EEE, MMM d, yyyy')}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BookingRow({ booking: b, cancelling, onCancel }) {
  return (
    <div
      data-testid={`booking-item-${b.id}`}
      className="border border-border/40 bg-surface p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:border-border/60"
    >
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-base font-medium text-white truncate" data-testid={`booking-resource-${b.id}`}>
          {b.resource_name}
        </h3>
        <div className="flex flex-wrap items-center gap-4 mt-2">
          <div className="flex items-center gap-1.5 text-text-secondary">
            <CalendarDays size={13} strokeWidth={1.5} />
            <span className="text-xs font-mono">{format(parseISO(b.date), 'EEE, MMM d, yyyy')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-text-secondary">
            <Clock size={13} strokeWidth={1.5} />
            <span className="text-xs font-mono">
              {b.time_slot}
              {b.duration_minutes > 15 && (() => {
                const [h, m] = b.time_slot.split(':').map(Number);
                const total = h * 60 + m + b.duration_minutes;
                const endH = Math.floor(total / 60).toString().padStart(2, '0');
                const endM = (total % 60).toString().padStart(2, '0');
                const dur = b.duration_minutes < 60 ? `${b.duration_minutes}min` : b.duration_minutes % 60 === 0 ? `${b.duration_minutes / 60}h` : `${Math.floor(b.duration_minutes / 60)}h${b.duration_minutes % 60}m`;
                return ` — ${endH}:${endM} (${dur})`;
              })()}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs font-mono tracking-wider uppercase px-2 py-1 border text-available border-available/30 bg-available/10" data-testid={`booking-status-${b.id}`}>
          {b.status}
        </span>
        <button
          onClick={() => onCancel(b.id)}
          disabled={cancelling === b.id}
          data-testid={`cancel-booking-${b.id}`}
          className="px-3 py-1.5 border border-booked/30 text-booked text-xs font-mono uppercase tracking-wider hover:bg-booked/10 transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {cancelling === b.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
          Cancel
        </button>
      </div>
    </div>
  );
}
